import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { tablesAPI, menuAPI, getErrorMessage } from '../services/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  // IMP-02: 2FA 2. adım state
  const [step, setStep] = useState('login'); // 'login' | 'totp'
  const [preToken, setPreToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const { login, completeLogin2FA } = useAuth();
  const { t, lang, setLang, SUPPORTED_LANGUAGES } = useLanguage();
  const navigate = useNavigate();

  // Restore remembered email on mount
  useEffect(() => {
    const remembered = localStorage.getItem('restaflex_remember_email');
    if (remembered) setEmail(remembered);
  }, []);

  const navigateByRole = async (user) => {
    if (user.role === 'superadmin') return navigate('/superadmin');
    if (user.role === 'chain_admin') return navigate('/chain');
    if (user.role === 'kitchen') return navigate('/kitchen');
    if (user.role === 'cashier') return navigate('/pos');

    if (user.role === 'admin') {
      try {
        const [tablesRes, itemsRes] = await Promise.all([
          tablesAPI.getAll(),
          menuAPI.getItems(),
        ]);
        const tables = tablesRes?.data?.data || tablesRes?.data || [];
        const items = itemsRes?.data?.data || itemsRes?.data || [];
        if ((Array.isArray(tables) ? tables.length : 0) === 0 ||
            (Array.isArray(items) ? items.length : 0) === 0) {
          return navigate('/onboarding');
        }
      } catch (_) { /* fallback to default page on API error */ }
    }

    const defaultPage = localStorage.getItem('restaflex_default_page') || 'dashboard';
    const pageRoutes = { dashboard: '/', tables: '/tables', orders: '/orders', kitchen: '/kitchen' };
    navigate(pageRoutes[defaultPage] || '/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // Tur 306: çift gönderim koruması
    if (!email?.trim() || !password) {
      setError(t('emailRequired'));
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError(t('formEmailInvalid') || t('invalidEmailAddress') || 'Geçerli email girin');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await login(email, password);
      // Persist or clear remembered email
      if (rememberMe) {
        localStorage.setItem('restaflex_remember_email', email);
      } else {
        localStorage.removeItem('restaflex_remember_email');
      }
      // IMP-02: 2FA gerekiyorsa 2. adıma geç
      if (result?.totp_required) {
        setPreToken(result.pre_token);
        setStep('totp');
        return;
      }
      navigateByRole(result);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit2FA = async (e) => {
    e.preventDefault();
    if (!totpCode) { setError(t('loginTotpRequired')); return; }
    setLoading(true);
    setError('');
    try {
      const user = await completeLogin2FA(preToken, totpCode);
      navigateByRole(user);
    } catch (err) {
      setError(getErrorMessage(err, t('loginCodeVerifyFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-8">
        {/* Language switcher */}
        <div className="flex justify-end gap-1 mb-4">
          {SUPPORTED_LANGUAGES.map(l => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              title={l.label}
              className={`px-2 py-1 rounded-lg text-xs font-semibold transition-colors ${
                lang === l.code
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {l.flag} {l.code.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🍴</div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">RestaFlex</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{t('loginTitle')}</p>
        </div>

        {error && (
          <div role="alert" aria-live="polite" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* IMP-02: 2FA 2. adım */}
        {step === 'totp' ? (
          <form onSubmit={handleSubmit2FA} className="space-y-4">
            <div className="text-center">
              <div className="text-4xl mb-2">🔐</div>
              <h2 className="font-bold text-gray-800 dark:text-gray-100">{t('loginTwoFactorTitle')}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('loginTwoFactorHint')}
              </p>
            </div>
            <input
              type="text"
              value={totpCode}
              onChange={e => setTotpCode(e.target.value)}
              placeholder="123456"
              maxLength={9}
              autoFocus
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-xl font-mono tracking-widest"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 rounded-lg font-medium transition-colors"
            >
              {loading ? t('loginVerifying') : t('loginVerifyAndEnter')}
            </button>
            <button
              type="button"
              onClick={() => { setStep('login'); setTotpCode(''); setPreToken(''); setError(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
            >
              ← {t('loginDifferentAccount')}
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4" aria-label={t('loginTitle')}>
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('email')}</label>
            <input
              id="login-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              aria-required="true"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('loginEmailPlaceholder') || 'ornek@restoran.com'}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white text-gray-900 dark:bg-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none text-sm"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('password')}</label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              aria-required="true"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white text-gray-900 dark:bg-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none text-sm"
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>{t('loginRememberMe') || 'Beni hatırla'}</span>
            </label>
            <Link to="/forgot-password" aria-label={t('loginForgotPasswordAria') || t('loginForgotPassword') || 'Şifremi unuttum'} className="text-sm text-blue-600 hover:underline focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none rounded">
              {t('loginForgotPassword') || 'Şifremi unuttum'}
            </Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            aria-busy={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 rounded-lg font-medium transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
          >
            {loading ? (t('loginSubmitting') || 'Giriş yapılıyor...') : t('loginBtn')}
          </button>
        </form>
        )}

        {step === 'login' && (
          process.env.NODE_ENV === 'development' ||
          (typeof window !== 'undefined' && (
            window.location.hostname === 'localhost' ||
            window.location.hostname.startsWith('staging.')
          ))
        ) && (
          <div
            role="group"
            aria-label={t('loginDemoGroupAria') || 'Demo hesap kısayolları'}
            className="mt-4 border-t border-dashed border-gray-200 dark:border-gray-700 pt-3"
          >
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('loginDemoTitle') || 'Demo Hesaplar'}
            </div>
            <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-2">
              {t('loginDemoDevOnly') || 'Sadece geliştirme ortamında görünür'}
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'superadmin', label: t('loginDemoSuperadmin') || 'Süperadmin', email: 'superadmin@restaflex.com', pass: 'admin123' },
                { key: 'admin',      label: t('loginDemoAdmin') || 'Admin (demo restoran)', email: 'admin@demo.restaflex.com', pass: 'demo123' },
                { key: 'waiter',     label: t('loginDemoWaiter') || 'Garson', email: 'waiter@demo.restaflex.com', pass: 'demo123' },
                { key: 'kitchen',    label: t('loginDemoKitchen') || 'Mutfak', email: 'kitchen@demo.restaflex.com', pass: 'demo123' },
              ].map(d => (
                <button
                  key={d.key}
                  type="button"
                  aria-label={`${t('loginDemoFill') || 'Demo hesap doldur'}: ${d.label}`}
                  onClick={() => { setEmail(d.email); setPassword(d.pass); }}
                  className="text-xs px-3 py-1 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none transition"
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
